import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Search } from 'lucide-react';
import apiClient from '../../utils/api';

const EXCEPTION_TYPES = ['DELAYED', 'LOST', 'DAMAGED', 'OTHER'];

const ExceptionBadge: React.FC<{ text: string }> = ({ text }) => {
  const colors: Record<string, string> = {
    DELAYED: '#f59e0b', LOST: '#ef4444', DAMAGED: '#f97316', OTHER: '#6366f1',
  };
  const color = colors[text] || '#64748b';
  return (
    <span style={{ background: color + '20', color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {text}
    </span>
  );
};

export const DispatchExceptions: React.FC = () => {
  const [shipments, setShipments] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modal, setModal] = useState<{ open: boolean; shipmentId: string; trackingNumber: string }>({ open: false, shipmentId: '', trackingNumber: '' });
  const [form, setForm] = useState({ type: 'DELAYED', description: '', location: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = { status: 'DEPARTED' };
      if (search) filters.search = search;
      const res = await apiClient.listShipments(filters, 50, 0);
      const d = res.data.data;
      // Also include IN_TRANSIT
      const filters2: any = { status: 'IN_TRANSIT' };
      if (search) filters2.search = search;
      const res2 = await apiClient.listShipments(filters2, 50, 0);
      const d2 = res2.data.data;
      const all = [
        ...(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : [])),
        ...(d2?.shipments ?? d2?.rows ?? (Array.isArray(d2) ? d2 : [])),
      ];
      setShipments(all);
    } catch {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openModal = (s: any) => {
    setForm({ type: 'DELAYED', description: '', location: '' });
    setError('');
    setModal({ open: true, shipmentId: s.id, trackingNumber: s.trackingNumber });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiClient.flagShipmentException(modal.shipmentId, form);
      setModal({ open: false, shipmentId: '', trackingNumber: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to flag exception');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Delivery Exceptions</h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking number or receiver..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
        </div>

        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          Showing active shipments (DEPARTED / IN_TRANSIT). Flag an exception to record delays, losses, or damage.
        </p>

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state"><h3>No active shipments found</h3></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Tracking #</th><th>Receiver</th><th>Destination</th><th>Transporter</th><th>Status</th><th>Departed</th><th>Action</th></tr>
              </thead>
              <tbody>
                {shipments.map((s: any) => (
                  <tr key={s.id}>
                    <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                    <td>{s.receiverName}</td>
                    <td>{s.receiverCity || '—'}</td>
                    <td>{s.transporter ? `${s.transporter.firstName} ${s.transporter.lastName}` : '—'}</td>
                    <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                    <td style={{ fontSize: 12 }}>{s.departureTime ? new Date(s.departureTime).toLocaleDateString() : '—'}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <Link to={`/dispatch/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                      <button className="btn btn-warning" style={{ padding: '3px 8px', fontSize: 12, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }} onClick={() => openModal(s)}>
                        <AlertTriangle size={12} /> Flag
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 440, maxWidth: '95vw', margin: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Flag Exception</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Shipment: <code style={{ fontSize: 12 }}>{modal.trackingNumber}</code>
            </p>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <form onSubmit={submit}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label>Exception Type *</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {EXCEPTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label>Description *</label>
                <textarea rows={3} required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue..." />
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Current Location (optional)</label>
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Musanze checkpoint" />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModal({ open: false, shipmentId: '', trackingNumber: '' })}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Saving...' : 'Flag Exception'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
